import * as React from "react";
import { Audit } from "./types";
import ReactDiff from "react-diff-viewer";
import { observer } from "mobx-react-lite";
import { X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "react-feather";
import { Theme } from "../../mobxStores/uiData/theme";
import { getAuditSubject } from "./utils";
import jsYaml from "js-yaml";
import { UserPreferences } from "../../mobxStores/userData/userPreferences";
import { NGButton } from "../../newcomponents/button/Button";
import { NGCheckbox } from "../../newcomponents/checkbox";
import { NGLabelText } from "../../newcomponents/text/labelText";
import { linksOf, request } from "../../services/cpln";
import { notification, Popconfirm } from "antd";
import { useDetailContext } from "../../components/detail/detailContext";

interface Props {
  audits: Audit[];
  onClose: () => void;
}
const AuditTrailDiffViewRaw: React.FC<Props> = ({ audits, onClose }) => {
  const { item, fetchItem } = useDetailContext();

  const { theme } = Theme;

  const [isApplying, setIsApplying] = React.useState(-1);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const previousIndex = currentIndex + 1;

  function bringOlder() {
    setCurrentIndex(Math.min(currentIndex + 1, audits.length - 2));
  }

  function bringOldest() {
    setCurrentIndex(audits.length - 2);
  }

  function bringNewer() {
    setCurrentIndex(Math.max(currentIndex - 1, 0));
  }

  function bringNewest() {
    setCurrentIndex(0);
  }

  async function applyVersion(_data: any) {
    const data = JSON.parse(JSON.stringify(_data));

    const selfLink = linksOf(data).self!;
    const version = data.version;

    delete data.id;
    delete data.version;
    delete data.links;
    delete data.created;
    delete data.lastModified;
    delete data.status;

    try {
      setIsApplying(version);
      await request({ method: "patch", url: selfLink, body: data });
      setIsApplying(-1);
      if (fetchItem) {
        fetchItem();
      }
    } catch (e) {
      setIsApplying(-1);
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({ message: "Failed to Apply Version", description: errorMessage });
    }
  }

  const canBringOlder = currentIndex < audits.length - 2;
  const canBringNewer = currentIndex > 0;
  const isOldest = currentIndex === audits.length - 2;
  const isNewest = currentIndex === 0;

  const currentAudit = audits[currentIndex];
  const current = currentAudit.resource.data ?? {};
  const previous = audits[previousIndex].resource.data ?? {};
  const subject = getAuditSubject(currentAudit);

  let title = "";
  if (previous?.version && current?.version) {
    title = `Version ${previous.version} - Version ${current.version}`;
  } else if (current?.version) {
    title = `Version ${current.version}`;
  } else if (previous?.version) {
    title = `Version ${previous.version}`;
  }

  return (
    <>
      <div className="flex items-center mb-4 gap-4">
        <NGButton
          variant={"secondary"}
          outlined
          className="basis-0 flex-grow"
          onClick={() => onClose()}
          renderIcon={(_, props) => <X {...props} />}
        >
          Close Diff View
        </NGButton>
        <NGButton
          variant={"primary"}
          className="basis-0 flex-grow"
          disabled={isOldest}
          onClick={bringOldest}
          renderIcon={(_, props) => <ChevronsLeft {...props} />}
          iconPosition={"left"}
        >
          Oldest
        </NGButton>
        <NGButton
          variant={"primary"}
          className="basis-0 flex-grow"
          disabled={!canBringOlder}
          onClick={bringOlder}
          renderIcon={(_, props) => <ChevronLeft {...props} />}
          iconPosition={"left"}
        >
          Previous
        </NGButton>
        <NGButton
          variant={"primary"}
          className="basis-0 flex-grow"
          disabled={!canBringNewer}
          onClick={bringNewer}
          renderIcon={(_, props) => <ChevronRight {...props} />}
        >
          Next
        </NGButton>
        <NGButton
          variant={"primary"}
          className="basis-0 flex-grow"
          disabled={isNewest}
          onClick={bringNewest}
          renderIcon={(_, props) => <ChevronsRight {...props} />}
        >
          Newest
        </NGButton>
      </div>
      <div className="text-sm diff-container">
        <div className="flex justify-between mb-4 text-base text-normal">
          <div className="flex items-center gap-6">
            <div>{title}</div>
            <NGCheckbox
              checked={UserPreferences.useSplitViewComparison}
              onChange={(value) => UserPreferences.setUseSplitViewComparison(value)}
            >
              <NGLabelText>Use Split View</NGLabelText>
            </NGCheckbox>
            {!!previous?.kind && previous?.kind !== "secret" ? (
              <Popconfirm
                title={`Apply Version ${previous?.version}`}
                description={`This action will override this object with a previous version. Are you sure you want to continue?`}
                onConfirm={() => applyVersion(previous)}
              >
                <NGButton
                  variant={`secondary`}
                  size={`small`}
                  disabled={isApplying !== -1}
                  loading={isApplying === previous?.version}
                >
                  Apply Version {previous?.version}
                </NGButton>
              </Popconfirm>
            ) : null}
            {!!current?.kind && current?.kind !== "secret" && current?.version !== item?.version ? (
              <Popconfirm
                title={`Apply Version ${current?.version}`}
                description={`This action will override this object with a previous version. Are you sure you want to continue?`}
                onConfirm={() => applyVersion(current)}
              >
                <NGButton
                  variant={`secondary`}
                  size={`small`}
                  disabled={isApplying !== -1}
                  loading={isApplying === current?.version}
                >
                  Apply Version {current?.version}
                </NGButton>
              </Popconfirm>
            ) : null}
          </div>
          <div>{subject.value}</div>
        </div>
        <div className="border" style={{ overflow: "auto" }}>
          <ReactDiff
            splitView={UserPreferences.useSplitViewComparison}
            oldValue={jsYaml.dump(previous ?? {}, { indent: 2 })}
            newValue={jsYaml.dump(current ?? {}, { indent: 2 })}
            useDarkTheme={theme === "dark"}
          />
        </div>
      </div>
    </>
  );
};

export const AuditTrailDiffView = observer(AuditTrailDiffViewRaw);
